import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  Stack,
  Paper,
  Pagination,
  Container,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import CharacterRankingsModal from "../components/CharacterRankingsModal";
import SEO from '../components/SEO';
import LazyImage from "../components/LazyImage"; // Add this import
import useMediaQuery from '@mui/material/useMediaQuery';

export default function Leaderboard() {
  const [mangaTags, setMangaTags] = useState([]);
  const [selectedScope, setSelectedScope] = useState("Global");
  const [selectedManga, setSelectedManga] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedType, setSelectedType] = useState("power_level");
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mangaDialogOpen, setMangaDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rankingsModalOpen, setRankingsModalOpen] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const itemsPerPage = 12;

  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  // Fetch manga tags (category = 'manga')
  useEffect(() => {
    const fetchMangaTags = async () => {
      const { data } = await supabase
        .from("leaderboard_tags")
        .select("tag, description, category, logo_url") // <-- Add logo_url
        .eq("category", "manga");
      setMangaTags(data || []);
    };
    fetchMangaTags();
  }, []);

  // Build the leaderboard query based on selected filters
  useEffect(() => {
    const fetchScores = async () => {
      setLoading(true);

      let tag = "Global";
      if (selectedScope === "Manga" && selectedManga) {
        tag = selectedManga;
      } else if (selectedScope === "CharacterType" && selectedRole) {
        tag = selectedRole === "Waifus" ? "Female" : selectedRole;
      }

      let query = supabase
        .from("character_tag_scores")
        .select("character_id, name, score, wins, losses, characters(image_url, manga)")
        .eq("tag", tag)
        .eq("type", selectedType);

      const { data, error } = await query.order("score", { ascending: false });

      setScores(data || []);
      setLoading(false);

      if (error) {
        console.error("Supabase error:", error);
      }
    };

    if (
      (selectedScope === "Global" && selectedType) ||
      (selectedScope === "Manga" && selectedManga && selectedType) ||
      (selectedScope === "CharacterType" && selectedRole && selectedType)
    ) {
      fetchScores();
    } else {
      setScores([]);
    }
  }, [selectedScope, selectedManga, selectedRole, selectedType]);

  // Handler for character type buttons
  const handleRoleSelect = (role) => {
    if (selectedScope === "CharacterType" && selectedRole === role) {
      setSelectedScope("Global");
      setSelectedRole(null);
      setSelectedManga(null);
    } else {
      setSelectedScope("CharacterType");
      setSelectedRole(role);
      setSelectedManga(null);
    }
  };

  // Handler for scope selection
  const handleScopeSelect = (scope) => {
    if (scope === "Global") {
      setSelectedScope("Global");
      setSelectedRole(null);
      setSelectedManga(null);
    } else if (scope === "Manga") {
      setMangaDialogOpen(true);
    }
  };

  // Add this utility function near the top (if not already present):
  function normalizeString(str) {
    return (str || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  // Filtered scores by search
  const filteredScores = scores.filter(row =>
    normalizeString(row.name).includes(normalizeString(search))
  );

  // Pagination logic
  const pageCount = Math.ceil(filteredScores.length / itemsPerPage);
  const paginatedScores = filteredScores.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Create a map of character_id to their rank in the full scores list
  const rankMap = {};
  scores.forEach((row, idx) => {
    rankMap[row.character_id] = idx + 1;
  });

  useEffect(() => {
    setPage(1);
  }, [selectedScope, selectedManga, selectedRole, selectedType, search]);

  // Create unique key for leaderboard content to trigger transitions
  const leaderboardKey = `${selectedScope}-${selectedManga || 'none'}-${selectedRole || 'none'}-${selectedType}-${search}-${page}`;

  // Animation variants
  const containerVariants = {
    hidden: { 
      opacity: 0,
      y: 20,
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeInOut",
        staggerChildren: 0.05,
      }
    },
    exit: { 
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.95,
      y: 20,
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const handleViewCharacterRankings = (character, characterData) => {
    // Combine the ranking data with character details
    const fullCharacterData = {
      ...character,
      id: character.character_id,
      image_filename: characterData?.image_url?.split('/').pop() || '',
      manga: characterData?.manga || '',
    };
    setSelectedCharacter(fullCharacterData);
    setRankingsModalOpen(true);
  };

  return (
    <>
      <SEO 
        title="Anime Character Rankings & Power Scaling Tier List"
        description="Discover the ultimate anime character rankings based on community votes. View power scaling tier lists and character battle statistics from manga and anime series."
        keywords="anime character rankings, character tier list, anime powerscaling tier list, manga character power levels, strongest anime characters"
        url="/leaderboard"
      />
      <Box sx={{ minHeight: "100vh" }}>
        {/* Spacer for navbar */}
        <Box sx={{ height: { xs: 72, sm: 80 } }} />

        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          {/* Title Section */}
          <Box sx={{ textAlign: "center", mb: { xs: 8, md: 12 } }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: 48, sm: 64, md: 72 },
                fontWeight: 900,
                background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textAlign: "center",
                mb: 4,
                letterSpacing: 2,
              }}
            >
              RANKINGS
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: "#e0e0e0",
                fontWeight: 600,
                fontSize: { xs: 18, sm: 22 },
                maxWidth: 600,
                mx: "auto",
                lineHeight: 1.4,
              }}
            >
              Discover the most powerful and popular manga characters
            </Typography>
          </Box>

          {/* Filter Controls Section */}
          <Box sx={{ 
            background: "rgba(35, 35, 35, 0.85)", // Made transparent like CharacterVoting
            borderRadius: 4,
            border: "2px solid #3e3e3e",
            p: { xs: 4, md: 6 },
            mb: { xs: 6, md: 8 },
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(10px)", // Add glass effect
            WebkitBackdropFilter: "blur(10px)",
          }}>
            {/* Scope selection */}
            <Box sx={{ mb: 6 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: "#fff", 
                  fontWeight: 700, 
                  mb: 4, 
                  textAlign: "center",
                  fontSize: { xs: 20, sm: 24 }
                }}
              >
                Select Category
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ justifyContent: "center" }}>
                <Button
                  variant={selectedScope === "Global" ? "contained" : "outlined"}
                  onClick={() => handleScopeSelect("Global")}
                  sx={{
                    height: { xs: 80, sm: 100 },
                    minWidth: { xs: "100%", sm: 200 },
                    borderRadius: 3,
                    fontSize: { xs: 18, sm: 24 },
                    fontWeight: 700,
                    px: 4,
                    color: selectedScope === "Global" ? "#181818" : "#fff",
                    borderColor: selectedScope === "Global" ? "transparent" : "#555",
                    background: selectedScope === "Global" 
                      ? "linear-gradient(45deg, #f49330 30%, #fff176 90%)" 
                      : "transparent",
                    boxShadow: selectedScope === "Global" 
                      ? "0 4px 20px rgba(244, 147, 48, 0.4)" 
                      : "none",
                    "&:hover": {
                      background: selectedScope === "Global" 
                        ? "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)" 
                        : "rgba(244, 147, 48, 0.1)",
                      borderColor: "#f49330",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Global
                </Button>
                
                {selectedScope === "Manga" && selectedManga ? (
                  <Button
                    onClick={() => setMangaDialogOpen(true)}
                    sx={{
                      height: { xs: 80, sm: 100 },
                      minWidth: { xs: "100%", sm: 200 },
                      borderRadius: 3,
                      p: 0,
                      background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                      boxShadow: "0 4px 20px rgba(244, 147, 48, 0.4)",
                      "&:hover": { 
                        background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Box
                      component="img"
                      src={`https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/logos/${selectedManga}_logo.webp`}
                      alt={selectedManga}
                      sx={{
                        height: { xs: 64, sm: 84 },
                        width: { xs: 148, sm: 195 },
                        aspectRatio: "21/9",
                        objectFit: "contain",
                        background: "#111",
                        borderRadius: 2,
                      }}
                    />
                  </Button>
                ) : (
                  <Button
                    variant={selectedScope === "Manga" ? "contained" : "outlined"}
                    onClick={() => handleScopeSelect("Manga")}
                    sx={{
                      height: { xs: 80, sm: 100 },
                      minWidth: { xs: "100%", sm: 200 },
                      borderRadius: 3,
                      fontSize: { xs: 18, sm: 24 },
                      fontWeight: 700,
                      px: 4,
                      color: selectedScope === "Manga" ? "#181818" : "#fff",
                      borderColor: selectedScope === "Manga" ? "transparent" : "#555",
                      background: selectedScope === "Manga" 
                        ? "linear-gradient(45deg, #f49330 30%, #fff176 90%)" 
                        : "transparent",
                      boxShadow: selectedScope === "Manga" 
                        ? "0 4px 20px rgba(244, 147, 48, 0.4)" 
                        : "none",
                      "&:hover": {
                        background: selectedScope === "Manga" 
                          ? "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)" 
                          : "rgba(244, 147, 48, 0.1)",
                        borderColor: "#f49330",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Manga
                  </Button>
                )}
              </Stack>
            </Box>
            
            {/* Character Type filter buttons */}
            <Box sx={{ mb: 6 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: "#fff", 
                  fontWeight: 700, 
                  mb: 3, 
                  textAlign: "center",
                  fontSize: { xs: 16, sm: 18 }
                }}
              >
                Character Types
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ justifyContent: "center" }}>
                {["Main Character", "Antagonist", "Waifus"].map((role) => (
                  <Button
                    key={role}
                    variant={selectedScope === "CharacterType" && selectedRole === role ? "contained" : "outlined"}
                    onClick={() => handleRoleSelect(role)}
                    sx={{
                      height: 56,
                      px: 3,
                      borderRadius: 2,
                      fontSize: 16,
                      fontWeight: 600,
                      color: selectedScope === "CharacterType" && selectedRole === role ? "#181818" : "#fff",
                      borderColor: selectedScope === "CharacterType" && selectedRole === role ? "transparent" : "#555",
                      background: selectedScope === "CharacterType" && selectedRole === role 
                        ? "linear-gradient(45deg, #f49330 30%, #fff176 90%)" 
                        : "transparent",
                      "&:hover": {
                        background: selectedScope === "CharacterType" && selectedRole === role 
                          ? "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)" 
                          : "rgba(244, 147, 48, 0.1)",
                        borderColor: "#f49330",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {role}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Box>
          
          {/* Active Filter Display */}
          {(selectedScope !== "Global" || search) && (
            <Box sx={{ 
              textAlign: "center", 
              mb: 6, 
              p: 3, 
              background: "rgba(244, 147, 48, 0.1)",
              borderRadius: 3,
              border: "1px solid rgba(244, 147, 48, 0.3)"
            }}>
              {selectedScope === "Manga" && selectedManga && (
                <Typography sx={{ fontWeight: 600, color: "#f49330", fontSize: 18 }}>
                  🎌 Manga: {selectedManga}
                </Typography>
              )}
              {selectedScope === "CharacterType" && selectedRole && (
                <Typography sx={{ fontWeight: 600, color: "#f49330", fontSize: 18 }}>
                  👤 Character Type: {selectedRole}
                </Typography>
              )}
              {search && (
                <Typography sx={{ fontWeight: 600, color: "#f49330", fontSize: 18, mt: 1 }}>
                  🔍 Search: "{search}"
                </Typography>
              )}
            </Box>
          )}
          
          {/* Search and Ranking Type Controls - Moved closer to results */}
          <Box sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center",
            alignItems: { xs: "stretch", md: "center" },
            gap: 6,
            mb: 6,
            p: 4,
            background: "rgba(35, 35, 35, 0.85)", // Made transparent
            borderRadius: 3,
            border: "1px solid #444",
            maxWidth: 800,
            mx: "auto",
            backdropFilter: "blur(10px)", // Add glass effect
            WebkitBackdropFilter: "blur(10px)",
          }}>
            {/* Search bar */}
            <Box sx={{ flex: 1, maxWidth: { xs: "100%", md: 350 } }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: "#fff", 
                  fontWeight: 600, 
                  mb: 2, 
                  fontSize: 16,
                  textAlign: { xs: "left", md: "center" }
                }}
              >
                Search Characters
              </Typography>
              <input
                type="text"
                placeholder="Search by character name..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: 16,
                  borderRadius: 8,
                  border: "2px solid #555",
                  background: "#1a1a1a",
                  color: "#fff",
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "border-color 0.3s ease",
                }}
                onFocus={(e) => e.target.style.borderColor = "#f49330"}
                onBlur={(e) => e.target.style.borderColor = "#555"}
              />
            </Box>
            
            {/* Ranking Type selection */}
            <Box sx={{ flex: 1, maxWidth: { xs: "100%", md: 350 } }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: "#fff", 
                  fontWeight: 600, 
                  mb: 2, 
                  fontSize: 16,
                  textAlign: "center"
                }}
              >
                Ranking Type
              </Typography>
              <Stack direction="row" spacing={2} sx={{ justifyContent: "center" }}>
                { [
                  { key: "power_level", label: "Power Level" },
                  { key: "popularity", label: "Popularity" }
                ].map((type) => (
                  <Button
                    key={type.key}
                    variant={selectedType === type.key ? "contained" : "outlined"}
                    onClick={() => setSelectedType(type.key)}
                    sx={{
                      height: 48,
                      flex: 1,
                      maxWidth: 140,
                      borderRadius: 2,
                      fontSize: 16,
                      fontWeight: 600,
                      px: 3,
                      color: selectedType === type.key ? "#181818" : "#fff",
                      borderColor: selectedType === type.key ? "transparent" : "#555",
                      background: selectedType === type.key 
                        ? "linear-gradient(45deg, #f49330 30%, #fff176 90%)" 
                        : "transparent",
                      boxShadow: selectedType === type.key 
                        ? "0 4px 16px rgba(244, 147, 48, 0.3)" 
                        : "none",
                      "&:hover": {
                        background: selectedType === type.key 
                          ? "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)" 
                          : "rgba(244, 147, 48, 0.1)",
                        borderColor: "#f49330",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {type.key === "power_level" ? (
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1 }}>
                        <span>POWER</span>
                        <span>LEVEL</span>
                      </Box>
                    ) : (
                      type.label
                    )}
                  </Button>
                )) }
              </Stack>
            </Box>
          </Box>
          
          {/* Animated Leaderboard display */}
          {loading && scores.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography sx={{ color: "#fff", textAlign: "center", fontSize: 24, py: 8 }}>
                Loading rankings...
              </Typography>
            </motion.div>
          ) : (
            <>
              <AnimatePresence mode="wait">
                <motion.div
                  key={leaderboardKey}
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ opacity: loading ? 0.6 : 1 }}
                >
                  <Grid container spacing={4} justifyContent="center">
                    {paginatedScores.map((row) => {
                      const wins = row.wins ?? 0;
                      const losses = row.losses ?? 0;
                      const total = wins + losses;
                      const winrate = total > 0 ? Math.round((wins / total) * 100) : 0;
                      const trueRank = rankMap[row.character_id];

                      return (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          xl={2}
                          key={row.character_id}
                          display="flex"
                          justifyContent="center"
                          sx={{
                            width: "100%",
                            maxWidth: 380,
                            minWidth: 0,
                          }}
                        >
                          <motion.div variants={itemVariants}>
                            <Paper
                              onClick={() => handleViewCharacterRankings(row, row.characters)}
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                p: 3,
                                borderRadius: 4,
                                minHeight: 360,
                                width: { xs: "100%", sm: 340, md: 380 },
                                maxWidth: "100%",
                                gap: 3,
                                background: "rgba(35, 35, 35, 0.85)",
                                border: "2px solid #3e3e3e",
                                position: "relative",
                                overflow: "hidden",
                                transition: "all 0.3s ease",
                                backdropFilter: "blur(10px)",
                                WebkitBackdropFilter: "blur(10px)",
                                cursor: "pointer",
                                "&:hover": {
                                  borderColor: "#f49330",
                                  boxShadow: "0 8px 32px rgba(244, 147, 48, 0.2)",
                                  transform: "translateY(-4px)",
                                  "& .see-more-text": {
                                    color: "#f49330",
                                    transform: "translateX(4px)",
                                  },
                                },
                              }}
                            >
                              {/* Rank Badge */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 12,
                                  left: 12,
                                  background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                                  color: "#181818",
                                  fontWeight: 700,
                                  fontSize: 14,
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: 2,
                                  zIndex: 1,
                                }}
                              >
                                #{trueRank}
                              </Box>

                              {/* "See more" text - Bottom right */}
                              <Typography
                                className="see-more-text"
                                sx={{
                                  position: "absolute",
                                  bottom: 12,
                                  right: 16,
                                  color: "#bbb",
                                  fontSize: 12,
                                  fontWeight: 500,
                                  transition: "all 0.3s ease",
                                  zIndex: 1,
                                }}
                              >
                                see more →
                              </Typography>

                              <LazyImage
                                src={row.characters?.image_url || ""}
                                alt={row.name}
                                threshold={0.2}
                                onLoad={() => console.log(`✅ Loaded ${row.name}`)}
                                sx={{
                                  width: 135,
                                  height: 240,
                                  borderRadius: 3,
                                  border: "2px solid #444",
                                  background: "#181818",
                                  boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                                  flexShrink: 0,
                                  objectFit: "cover",
                                }}
                              />

                              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: "#fff", mb: 1 }}>
                                  {row.name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#bbb", mb: 2 }}>
                                  {row.characters?.manga || ""}
                                </Typography>
                                <Box sx={{ 
                                  background: "rgba(244, 147, 48, 0.1)", 
                                  borderRadius: 2, 
                                  p: 2, 
                                  border: "1px solid rgba(244, 147, 48, 0.3)" 
                                }}>
                                  <Typography variant="body2" sx={{ color: "#f49330", fontWeight: 600, mb: 1 }}>
                                    {selectedType === "power_level" ? "Power Level" : "Popularity"}
                                  </Typography>
                                  <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
                                    {row.score}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "#e0e0e0" }}>
                                    Winrate: {winrate}% ({wins}W/{losses}L)
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>
                          </motion.div>
                        </Grid>
                      );
                    })}
                  </Grid>
                </motion.div>
              </AnimatePresence>
              
              {/* Pagination controls */}
              {pageCount > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'auto',
                    flexWrap: 'nowrap',
                    mt: 3,
                    mb: 2,
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'} // <-- Pass a string, not an object
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={isMobile ? 1 : 2}
                    sx={{
                      flexWrap: 'nowrap',
                      '.MuiPagination-ul': {
                        flexWrap: 'nowrap',
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
          
          {filteredScores.length === 0 && !loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ 
                textAlign: "center", 
                py: 8,
                background: "rgba(35, 35, 35, 0.85)", // Made transparent
                borderRadius: 4,
                border: "2px solid #3e3e3e",
                backdropFilter: "blur(10px)", // Add glass effect
                WebkitBackdropFilter: "blur(10px)",
              }}>
                <Typography sx={{ color: "#e0e0e0", fontSize: 24, fontWeight: 600 }}>
                  No characters found
                </Typography>
                <Typography sx={{ color: "#bbb", fontSize: 16, mt: 2 }}>
                  Try adjusting your search criteria
                </Typography>
              </Box>
            </motion.div>
          )}
        </Container>

        {/* Manga Selection Dialog */}
        <Dialog
          open={mangaDialogOpen}
          onClose={() => setMangaDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: "rgba(35, 35, 35, 0.85)", // Made transparent
              border: "2px solid #3e3e3e",
              borderRadius: 3,
              backdropFilter: "blur(10px)", // Add glass effect
              WebkitBackdropFilter: "blur(10px)",
            }
          }}
        >
          <DialogTitle sx={{ color: "#fff", textAlign: "center", fontSize: 24, fontWeight: 700 }}>
            Select Manga Series
          </DialogTitle>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {mangaTags.map((manga) => (
                <Grid item xs={6} sm={4} md={3} key={manga.tag}>
                  <Button
                    onClick={() => {
                      setSelectedScope("Manga");
                      setSelectedManga(manga.tag);
                      setSelectedRole(null);
                      setMangaDialogOpen(false);
                    }}
                    sx={{
                      width: "100%",
                      height: 100,
                      borderRadius: 3,
                      p: 1,
                      background: "transparent",
                      border: "2px solid #555",
                      "&:hover": {
                        borderColor: "#f49330",
                        background: "rgba(244, 147, 48, 0.1)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <img
                      src={manga.logo_url || `https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/logos/${manga.tag}_logo.webp`}
                      alt={manga.tag}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        borderRadius: 2,
                      }}
                      onError={e => { e.target.style.display = "none"; }}
                    />
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Dialog>

        {/* Character Rankings Modal */}
        <CharacterRankingsModal
          open={rankingsModalOpen}
          onClose={() => setRankingsModalOpen(false)}
          character={selectedCharacter}
        />
      </Box>
    </>
  );
}